import { useState, useEffect, useCallback } from "react";
import api from "../api";
import { t, ehSegmentoClinica } from "../vocab";
import { Package, Wrench, Boxes, DollarSign } from "lucide-react";

const fmt = (v) => `R$${Number(v||0).toLocaleString("pt-BR",{minimumFractionDigits:2})}`;

const th = { padding:"10px 12px", textAlign:"left", fontSize:11, fontWeight:700, color:"var(--text-secondary)", textTransform:"uppercase", letterSpacing:.4, borderBottom:"1px solid var(--border-color)", whiteSpace:"nowrap" };
const td = { padding:"10px 12px", fontSize:13, color:"var(--text-secondary)", borderBottom:"1px solid var(--border-light)", verticalAlign:"middle" };
const inp = { width:"100%", padding:"8px 10px", border:"1px solid var(--border-color)", borderRadius:6, fontSize:13, outline:"none", boxSizing:"border-box" };
const lbl = { display:"block", fontSize:11, fontWeight:700, color:"var(--text-secondary)", textTransform:"uppercase", letterSpacing:.4, marginBottom:4 };

function Btn({ children, onClick, cor="var(--text-secondary)", full, tipo="button" }) {
  return <button type={tipo} onClick={onClick} style={{ padding:"6px 12px", fontSize:12, fontWeight:600, color:full?"var(--card-bg)":cor, background:full?cor:"transparent", border:`1px solid ${cor}${full?"":"66"}`, borderRadius:6, cursor:"pointer", whiteSpace:"nowrap" }}>{children}</button>;
}

function Card({ n, label, cor, Icon, ativo, onClick }) {
  return (
    <div onClick={onClick} style={{ flex:1, minWidth:150, padding:"16px 18px", borderRadius:10, background:cor+"0F", border:`1px solid ${ativo?cor:cor+"33"}`, cursor:onClick?"pointer":"default", display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
      <div>
        <div style={{ fontSize:22, fontWeight:700, color:cor, lineHeight:1.1 }}>{n}</div>
        <div style={{ fontSize:11, fontWeight:700, color:"var(--text-secondary)", textTransform:"uppercase", letterSpacing:.5, marginTop:4 }}>{label}</div>
      </div>
      {Icon && <Icon size={18} color={cor} style={{ opacity:.5 }} />}
    </div>
  );
}

const VAZIO = { codigo:"", nome:"", tipo:"produto", categoria:"", marca:"", ncm:"", unidade:"un", custo:0, margem:0, preco:0, duracaoMin:0, sessoesPadrao:0, descricao:"", ativo:true };

export default function Produtos() {
  const [lista, setLista] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [busca, setBusca] = useState("");
  const [fTipo, setFTipo] = useState("");
  const [fCat, setFCat] = useState("");
  const [modal, setModal] = useState(null);
  const [load, setLoad] = useState(false);

  const carregar = useCallback(async () => {
    setLoad(true);
    try {
      const [r, rc] = await Promise.all([
        api.get("/catalogo?todos=1"),
        api.get("/catalogo/categorias"),
      ]);
      setLista(Array.isArray(r.data) ? r.data : []);
      setCategorias(Array.isArray(rc.data) ? rc.data : []);
    } catch (e) { console.error("Erro ao carregar catalogo", e); }
    setLoad(false);
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const visiveis = lista.filter(p => {
    if (fTipo && p.tipo !== fTipo) return false;
    if (fCat && p.categoria !== fCat) return false;
    if (busca) {
      const b = busca.toLowerCase();
      const alvo = `${p.nome} ${p.codigo} ${p.marca} ${p.categoria} ${p.ncm || ""}`.toLowerCase();
      if (!alvo.includes(b)) return false;
    }
    return true;
  });

  const excluir = async (id) => {
    if (!window.confirm("Excluir este item do catálogo?")) return;
    try { await api.delete(`/catalogo/${id}`); carregar(); }
    catch (e) { alert("Erro ao excluir"); }
  };

  const nProdutos = lista.filter(p => p.tipo === "produto").length;
  const nServicos = lista.filter(p => p.tipo === "servico").length;

  return (
    <div style={{ padding:"24px 28px", background:"var(--page-bg)", minHeight:"100vh" }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
        <div>
          <h1 style={{ fontSize:26, fontWeight:800, color:"var(--text-primary)", margin:0 }}>{t("Produtos e Serviços")}</h1>
          <p style={{ fontSize:13, color:"var(--text-secondary)", margin:"4px 0 0" }}>{t("Catálogo usado nos orçamentos")}</p>
        </div>
        <Btn onClick={() => setModal({ ...VAZIO })} cor="#EE6A1A" full>+ Novo item</Btn>
      </div>

      <div style={{ display:"flex", gap:12, marginBottom:16, flexWrap:"wrap" }}>
        <Card n={lista.length} label="Total no catálogo" cor="#98A2B3" Icon={Boxes} ativo={!fTipo} onClick={() => setFTipo("")} />
        <Card n={nProdutos} label="Produtos" cor="#579bfc" Icon={Package} ativo={fTipo==="produto"} onClick={() => setFTipo("produto")} />
        <Card n={nServicos} label="Serviços" cor="#a25ddc" Icon={Wrench} ativo={fTipo==="servico"} onClick={() => setFTipo("servico")} />
        <Card n={categorias.length} label="Categorias" cor="#1D9E75" Icon={DollarSign} />
      </div>

      <div style={{ display:"flex", gap:10, marginBottom:14, flexWrap:"wrap" }}>
        <input
          style={{ ...inp, maxWidth:340 }}
          placeholder="Buscar por nome, código, marca ou categoria..."
          value={busca}
          onChange={e => setBusca(e.target.value)}
        />
        <select style={{ ...inp, maxWidth:220 }} value={fCat} onChange={e => setFCat(e.target.value)}>
          <option value="">Todas as categorias</option>
          {categorias.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        {(busca || fCat || fTipo) && (
          <Btn onClick={() => { setBusca(""); setFCat(""); setFTipo(""); }} cor="#98A2B3">Limpar filtros</Btn>
        )}
      </div>

      <div style={{ background:"var(--card-bg)", borderRadius:10, border:"1px solid var(--border-color)", overflow:"auto" }}>
        <table style={{ width:"100%", borderCollapse:"collapse" }}>
          <thead>
            <tr>
              <th style={{ ...th, width:40, textAlign:"center" }}>#</th>
              <th style={{ ...th, width:100 }}>Código</th>
              <th style={th}>Nome</th>
              <th style={{ ...th, width:100 }}>Tipo</th>
              <th style={th}>Categoria</th>
              <th style={th}>Marca</th>
              <th style={{ ...th, width:90 }}>NCM</th>
              <th style={{ ...th, width:60 }}>Un.</th>
              <th style={{ ...th, width:100, textAlign:"right" }}>Custo</th>
              <th style={{ ...th, width:80, textAlign:"right" }}>Margem</th>
              <th style={{ ...th, width:110, textAlign:"right" }}>Venda</th>
              <th style={{ ...th, width:150, textAlign:"center" }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {visiveis.map((p, i) => (
              <tr key={p._id} style={{ opacity: p.ativo === false ? .45 : 1 }}>
                <td style={{ ...td, textAlign:"center", color:"#98A2B3" }}>{i+1}</td>
                <td style={{ ...td, color:"var(--text-secondary)", fontFamily:"monospace" }}>{p.codigo || "—"}</td>
                <td style={{ ...td, fontWeight:600, color:"var(--text-primary)" }}>{p.nome}</td>
                <td style={td}>
                  <span style={{ display:"inline-block", padding:"3px 10px", borderRadius:12, fontSize:11, fontWeight:600,
                    color: p.tipo === "servico" ? "#a25ddc" : "#579bfc",
                    background: (p.tipo === "servico" ? "#a25ddc" : "#579bfc") + "1A",
                    border: `1px solid ${(p.tipo === "servico" ? "#a25ddc" : "#579bfc")}40` }}>
                    {p.tipo === "servico" ? "Serviço" : "Produto"}
                  </span>
                </td>
                <td style={td}>{p.categoria || "—"}</td>
                <td style={td}>{p.marca || "—"}</td>
                <td style={{ ...td, fontFamily:"monospace", color:"var(--text-secondary)" }}>{p.ncm || "—"}</td>
                <td style={td}>{p.unidade}</td>
                <td style={{ ...td, textAlign:"right", color:"var(--text-secondary)" }}>{p.custo ? fmt(p.custo) : "—"}</td>
                <td style={{ ...td, textAlign:"right", color:"var(--text-secondary)" }}>{p.margem ? `${p.margem}%` : "—"}</td>
                <td style={{ ...td, textAlign:"right", fontWeight:700, color:"#1D9E75" }}>{fmt(p.preco)}</td>
                <td style={{ ...td, textAlign:"center" }}>
                  <div style={{ display:"flex", gap:6, justifyContent:"center" }}>
                    <Btn onClick={() => setModal({ ...p })} cor="#579bfc">Editar</Btn>
                    <Btn onClick={() => excluir(p._id)} cor="#E24B4A">Excluir</Btn>
                  </div>
                </td>
              </tr>
            ))}
            {!visiveis.length && (
              <tr><td colSpan={12} style={{ ...td, textAlign:"center", color:"#98A2B3", padding:30 }}>
                {load ? "Carregando..." : "Nenhum item. Clique em + Novo item para cadastrar."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {modal && (
        <ModalItem
          dados={modal}
          categorias={categorias}
          onFechar={() => setModal(null)}
          onSalvo={() => { setModal(null); carregar(); }}
        />
      )}
    </div>
  );
}

function ModalItem({ dados, categorias, onFechar, onSalvo }) {
  const [f, setF] = useState({ ...VAZIO, ...dados });
  const [salvando, setSalvando] = useState(false);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  // custo + margem => preço sugerido
  const aplicarMargem = () => {
    const c = Number(f.custo) || 0;
    const m = Number(f.margem) || 0;
    if (!c) return;
    set("preco", Number((c * (1 + m / 100)).toFixed(2)));
  };

  const margemReal = (() => {
    const c = Number(f.custo) || 0;
    const p = Number(f.preco) || 0;
    if (!c || !p) return null;
    return (((p - c) / c) * 100).toFixed(1);
  })();

  const salvar = async () => {
    if (!f.nome) return alert("Informe o nome.");
    setSalvando(true);
    const payload = {
      ...f,
      custo: Number(f.custo) || 0,
      margem: Number(f.margem) || 0,
      preco: Number(f.preco) || 0,
    };
    delete payload._id;
    delete payload.createdAt;
    delete payload.updatedAt;
    delete payload.__v;
    try {
      if (dados._id) await api.put(`/catalogo/${dados._id}`, payload);
      else await api.post("/catalogo", payload);
      onSalvo();
    } catch (e) {
      alert("Erro ao salvar: " + (e?.response?.data?.erro || e.message));
    }
    setSalvando(false);
  };

  return (
    <div onClick={onFechar} style={{ position:"fixed", inset:0, background:"rgba(16,24,40,.55)", display:"flex", alignItems:"flex-start", justifyContent:"center", zIndex:1000, padding:"40px 16px", overflow:"auto" }}>
      <div onClick={e => e.stopPropagation()} style={{ background:"var(--card-bg)", borderRadius:12, width:"100%", maxWidth:720, padding:24, boxShadow:"0 20px 60px rgba(0,0,0,.25)" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:18 }}>
          <h2 style={{ margin:0, fontSize:19, fontWeight:800, color:"var(--text-primary)" }}>
            {dados._id ? t("Editar item") : t("Novo item do catálogo")}
          </h2>
          <button onClick={onFechar} style={{ border:"none", background:"none", fontSize:22, color:"#98A2B3", cursor:"pointer", lineHeight:1 }}>×</button>
        </div>

        <div style={{ display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap:12, marginBottom:14 }}>
          <div>
            <label style={lbl}>Código / SKU</label>
            <input style={inp} value={f.codigo} onChange={e => set("codigo", e.target.value)} placeholder={t("Ex: CAM-4231")} />
          </div>
          <div style={{ gridColumn:"span 3" }}>
            <label style={lbl}>Nome *</label>
            <input style={inp} value={f.nome} onChange={e => set("nome", e.target.value)} placeholder={t("Ex: Câmera IP Bullet 4MP")} />
          </div>

          <div>
            <label style={lbl}>Tipo</label>
            <select style={inp} value={f.tipo} onChange={e => set("tipo", e.target.value)}>
              <option value="produto">Produto</option>
              <option value="servico">Serviço</option>
            </select>
          </div>
          <div>
            <label style={lbl}>Categoria</label>
            <input style={inp} list="cats" value={f.categoria} onChange={e => set("categoria", e.target.value)} placeholder={t("Ex: Câmeras")} />
            <datalist id="cats">
              {categorias.map(c => <option key={c} value={c} />)}
            </datalist>
          </div>
          {!ehSegmentoClinica() && (<div>
            <label style={lbl}>Marca</label>
            <input style={inp} value={f.marca} onChange={e => set("marca", e.target.value)} placeholder={t("Ex: Intelbras")} />
          </div>)}
          <div>
            <label style={lbl}>Unidade</label>
            <input style={inp} value={f.unidade} onChange={e => set("unidade", e.target.value)} placeholder="un, m, h..." />
          </div>
          {!ehSegmentoClinica() && (<div>
            <label style={lbl}>NCM</label>
            <input
              style={{ ...inp, fontFamily:"monospace" }}
              value={f.ncm}
              maxLength={10}
              onChange={e => {
                const d = e.target.value.replace(/\D/g, "").slice(0, 8);
                const fmtNcm = d.length > 6 ? `${d.slice(0,4)}.${d.slice(4,6)}.${d.slice(6)}`
                  : d.length > 4 ? `${d.slice(0,4)}.${d.slice(4)}`
                  : d;
                set("ncm", fmtNcm);
              }}
              placeholder="8525.80.19"
            />
          </div>)}
        </div>

        <div style={{ background:"var(--page-bg)", border:"1px solid var(--border-color)", borderRadius:8, padding:14, marginBottom:14 }}>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr auto 1fr", gap:12, alignItems:"end" }}>
            <div>
              <label style={lbl}>Custo (R$)</label>
              <input type="number" min="0" step="any" style={inp} value={f.custo} onChange={e => set("custo", e.target.value)} />
            </div>
            <div>
              <label style={lbl}>Margem (%)</label>
              <input type="number" min="0" step="any" style={inp} value={f.margem} onChange={e => set("margem", e.target.value)} />
            </div>
            <Btn onClick={aplicarMargem} cor="#579bfc">Calcular venda →</Btn>
            <div>
              <label style={lbl}>Preço de venda (R$) *</label>
              <input type="number" min="0" step="any" style={{ ...inp, fontWeight:700, color:"#1D9E75" }} value={f.preco} onChange={e => set("preco", e.target.value)} />
            </div>
          </div>
          {margemReal !== null && (
            <div style={{ fontSize:11, color:"var(--text-secondary)", marginTop:8 }}>
              Margem real sobre o custo: <strong style={{ color: Number(margemReal) >= 0 ? "#1D9E75" : "#E24B4A" }}>{margemReal}%</strong>
              {" · "}Lucro por unidade: <strong>{fmt((Number(f.preco)||0) - (Number(f.custo)||0))}</strong>
            </div>
          )}
        </div>

        {ehSegmentoClinica() && (
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:14 }}>
            <div>
              <label style={lbl}>Duração da sessão (min)</label>
              <input type="number" min="0" step="5" style={inp} value={f.duracaoMin} onChange={e => set("duracaoMin", e.target.value)} placeholder="Ex: 60" />
            </div>
            <div>
              <label style={lbl}>Sessões do pacote</label>
              <input type="number" min="0" step="1" style={inp} value={f.sessoesPadrao} onChange={e => set("sessoesPadrao", e.target.value)} placeholder="Ex: 10" />
            </div>
          </div>
        )}

        <div style={{ marginBottom:14 }}>
          <label style={lbl}>Descrição (opcional)</label>
          <textarea style={{ ...inp, minHeight:60, resize:"vertical" }} value={f.descricao} onChange={e => set("descricao", e.target.value)} placeholder={t("Detalhes técnicos, especificações...")} />
        </div>

        <label style={{ display:"flex", alignItems:"center", gap:8, fontSize:13, color:"var(--text-secondary)", marginBottom:18, cursor:"pointer" }}>
          <input type="checkbox" checked={f.ativo !== false} onChange={e => set("ativo", e.target.checked)} />
          Item ativo (aparece na busca ao montar orçamentos)
        </label>

        <div style={{ display:"flex", justifyContent:"flex-end", gap:10 }}>
          <Btn onClick={onFechar} cor="#98A2B3">Cancelar</Btn>
          <Btn onClick={salvar} cor="#EE6A1A" full>{salvando ? "Salvando..." : "Salvar item"}</Btn>
        </div>
      </div>
    </div>
  );
}
